// MainstayMessages.jsx
import React from "react";
import { Grid, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import { Radio, RadioGroup, FormControlLabel, Checkbox } from "@mui/material";
import ReactTable from "react-table";
import moment from "moment";
import * as mainstayApi from "../../api/mainstay";

function pickWhen(m) {
    return m?.sentAt || m?.receivedAt || m?.createdAt || null;
}
function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}
function firstDefined(obj, ...keys) {
    for (const k of keys) {
        const v = obj?.[k];
        if (v !== undefined && v !== null) return v;
    }
    return null;
}
function fmtWhen(m) {
    const iso = pickWhenRaw(m);
    return iso ? moment(iso).format("M/D/YY h:mm a") : "—";
}
function pickWhenRaw(m) {
    // tolerate snake/camel from the server
    return (
        firstDefined(m, "created", "created") ||
        firstDefined(m, "sentAt", "sent_at") ||
        firstDefined(m, "receivedAt", "received_at") ||
        
        null
    );
}
function toUtcIso(d) {
    if (!d) return null;
    if (typeof d === "string") return d;
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString();
}
function getDateDaysAgo(days) {
    const d = new Date();
    d.setDate(d.getDate() - days);
    d.setHours(0, 0, 1, 0);
    return d;
}
function endOfTodayPlus1s() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 1, 0);
    return d;
}
function generateDateChoices() {
    const end = endOfTodayPlus1s();
    return [
        { label: "Last 24 Hours", beginDate: getDateDaysAgo(1), endDate: end },
        { label: "Last 72 Hours", beginDate: getDateDaysAgo(3), endDate: end },
        { label: "Last 120 Hours", beginDate: getDateDaysAgo(5), endDate: end },
        { label: "Last 30 Days", beginDate: getDateDaysAgo(30), endDate: end },
        { label: "Last 60 Days", beginDate: getDateDaysAgo(60), endDate: end },
        { label: "Last 90 Days", beginDate: getDateDaysAgo(90), endDate: end },
    ];
}

export default class Chat extends React.Component {
    static defaultProps = {
        contactId: null,
        limit: 50,
        autoLoad: true,
    };

    constructor(props) {
        super(props);
        const dateChoices = generateDateChoices();
        this.state = {
            loading: false,
            error: null,
            items: [],
            next: null,
            loadedOnce: false,

            
            selectedDateLabel: dateChoices[0].label,
            dateChoices,
            limit: props.limit,
            searchText: "",
            direction: "all",
        };
        this._mounted = false;
    }

    componentDidMount() {
        this._mounted = true;
        if (this.props.autoLoad) this.fetchInitial();
    }
    componentWillUnmount() {
        this._mounted = false;
    }

    getSelectedDates = () => {
        const { dateChoices, selectedDateLabel } = this.state;
        const found = dateChoices.find(d => d.label === selectedDateLabel) || dateChoices[0];
        return {
            sinceUtc: toUtcIso(found.beginDate),
            untilUtc: toUtcIso(found.endDate),
        };
    };

    callListApi = async (cursor = null) => {
        const { contactId } = this.props;
        const { limit } = this.state;
        const { sinceUtc, untilUtc } = this.getSelectedDates();

        if (contactId) {
            // Keep your existing shape for contact-specific calls
            return await mainstayApi.getMessagesForContact(contactId, { limit, sinceUtc, untilUtc, cursor });
        }

        const input = { limit, sinceUtc, untilUtc, cursor }
        return await mainstayApi.getMessages(input);
    };

    fetchInitial = async () => {
        if (!this._mounted) return;

        this.setState({ loading: true, error: null, items: [], next: null });
        // Removed manual sync
        //await mainstayApi.syncChat();
        try {
            const page = await this.callListApi(null);
            if (!this._mounted) return;
            //console.log(page);
            // ABP unwrap already happens in your api layer; page is expected to be the payload
            this.setState({
                items: page?.data.result || [],
                next: page?.next || null,
                loading: false,
                loadedOnce: true,
            });
        } catch (err) {
            if (!this._mounted) return;
            this.setState({
                error: err?.message || "Failed to load messages.",
                loading: false,
                loadedOnce: true,
            });
        }
    };

    fetchMore = async () => {
        if (!this._mounted) return;
        const { next } = this.state;
        if (!next) return;

        this.setState({ loading: true, error: null });
        try {
            const page = await this.callListApi(next);
            if (!this._mounted) return;
            //console.log(page);
            this.setState(s => ({
                items: [...(s.items || []), ...(page?.data.result || [])],
                next: page?.next || null,
                loading: false,
            }));
        } catch (err) {
            if (!this._mounted) return;
            this.setState({
                error: err?.message || "Failed to load more messages.",
                loading: false,
            });
        }
    };


    applyClientFilters = (rows) => {
        const { searchText, direction } = this.state;
        const q = (searchText || "").trim().toLowerCase();
        if (!rows || rows.length === 0) return [];

        let out = rows;

        if (direction !== "all") {
            out = out.filter(r => (r.direction || "").toLowerCase() === direction);
        }

        if (q) {
            out = out.filter(r => {
                // prefer body, then text, then extra.body
                const body =
                    (r.body ?? r.text ?? "") ||
                    String(r?.extra?.body ?? "");
                const who =
                    `${r?.contactId || ""} ${r?.outgoingSource || ""} ${r?.liveChatUserEmail || ""} ${r?.understandingTopic || ""} ${r?.advisor || ""} ${r?.userProfile?.firstName || ""} ${r?.userProfile?.lastName || ""}`;

                return (
                    body.toLowerCase().includes(q) ||
                    who.toLowerCase().includes(q)
                );
            });
        }

        return out;
    };

    // -------- UI handlers --------
    onDateChange = (e) => this.setState({ selectedDateLabel: e.target.value });
    onDirectionChange = (e) => this.setState({ direction: e.target.value });
    onSearchChange = (e) => this.setState({ searchText: e.target.value });
    onLimitChange = (e) => this.setState({ limit: Number(e.target.value) });
    onApply = () => this.fetchInitial();

    renderToolbar() {
        const { loading, dateChoices, selectedDateLabel, limit, searchText, direction } = this.state;

        return (
            <Grid container spacing={2} style={{ marginBottom: 12 }}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6">Dates</Typography>
                    <RadioGroup value={selectedDateLabel} onChange={this.onDateChange} style={{ flexDirection: "row", flexWrap: "wrap" }}>
                        {dateChoices.map((d, idx) => (
                            <FormControlLabel key={idx} value={d.label} control={<Radio />} label={d.label} />
                        ))}
                    </RadioGroup>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6">Filters</Typography>

                    <RadioGroup value={direction} onChange={this.onDirectionChange} style={{ flexDirection: "row" }}>
                        <FormControlLabel value="all" control={<Radio />} label="All" />
                        <FormControlLabel value="incoming" control={<Radio />} label="Incoming" />
                        <FormControlLabel value="outgoing" control={<Radio />} label="Outgoing" />
                    </RadioGroup>

                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                        

                        <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Search text / contact / source / topic"
                            value={searchText}
                            onChange={this.onSearchChange}
                            style={{ flex: 1 }}
                        />

                        <Button onClick={this.onApply} disabled={loading} variant="contained" color="primary">
                            {loading ? "Loading…" : "Apply"}
                        </Button>
                    </div>
                </Grid>
            </Grid>
        );
    }

    renderTable() {
        const data = this.applyClientFilters(this.state.items);

        const columns = [

            {
                Header: "First Name",
                accessor: "userProfile.firstName",
                Cell: row => (
                    <div className="dataCell" style={{ textAlign: "center" }}>{row.original.userProfile.firstName}</div>
                ),

            },
            {
                Header: "Last Name",
                accessor: "userProfile.lastName",
                Cell: row => (
                    <div className="dataCell" style={{ textAlign: "center" }}>{row.original.userProfile.lastName}</div>
                ),

            },
            {
                Header: "When",
                id: "created",
                accessor: (m) => m.created ?? m.createdAt ?? m.sentAt ?? m.receivedAt ?? "",
                Cell: ({ original }) => <div style={{ textAlign: "center" }}>{fmtWhen(original)}</div>,
                width: 160,
                filterMethod: (filter, row) =>
                    (row[filter.id] || "").toLowerCase().includes((filter.value || "").toLowerCase()),
            },
            {
                Header: "Direction",
                id: "direction",
                accessor: (m) => (m.direction || "").toLowerCase(),
                width: 110,
                Cell: ({ value }) => (
                    <div style={{ textAlign: "center", fontWeight: 600 }}>
                        {value === "incoming" ? "← Incoming" : value === "outgoing" ? "→ Outgoing" : "—"}
                    </div>
                ),
                filterMethod: (filter, row) =>
                    (row[filter.id] || "").includes((filter.value || "").toLowerCase()),
            },
            {
                Header: "Body",
                id: "body",
                accessor: (m) => m.body ?? m.text ?? "",
                Cell: ({ value }) => <div style={{ whiteSpace: "pre-wrap" }}>{value || "—"}</div>,
                minWidth: 300,
                filterMethod: (filter, row) =>
                    (row[filter.id] || "").toLowerCase().includes((filter.value || "").toLowerCase()),
            },
           
            {
                Header: "Source",
                id: "outgoingSource",
                accessor: (m) => m.outgoingSource || "User",
                width: 140,
                Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value || "—"}</div>,
                filterMethod: (filter, row) =>
                    (row[filter.id] || "").toLowerCase().includes((filter.value || "").toLowerCase()),
            },
            {
                Header: "Topic",
                id: "understandingTopic",
                accessor: (m) => m.understandingTopic || "",
                width: 200,
                Cell: ({ value }) => <div style={{ textAlign: "center" }}>{value || "—"}</div>,
                filterMethod: (filter, row) =>
                    (row[filter.id] || "").toLowerCase().includes((filter.value || "").toLowerCase()),
            },
            {
                Header: "Coach",
                accessor: "advisor",
                Cell: row => (
                    <div className="dataCell" style={{ textAlign: "center" }}>{row.original.advisor}</div>
                ),

            }
        ];

        return (
            
            <ReactTable
                data={data}
                columns={columns}
                defaultPageSize={50}
                minRows={0}
                filterable
                className="-striped -highlight tableWrapper"
            />
        );
    }

    renderFooter() {
        const { loading, next } = this.state;
        return (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
                {next ? (
                    <Button onClick={this.fetchMore} disabled={loading} variant="outlined">
                        {loading ? "Loading…" : "Load more"}
                    </Button>
                ) : (
                    <div style={{ color: "#888", fontSize: 12 }}>{loading ? "" : "End of results"}</div>
                )}
            </div>
        );
    }

    render() {
        const { error, loading, loadedOnce } = this.state;
        //console.log(this);
        return (
            <div style={{ marginRight: 12 }}>
                {this.renderToolbar()}

                {error && (
                    <div style={{ marginBottom: 12, color: "#b00020" }}>
                        {error}
                    </div>
                )}

                {!loadedOnce && loading ? <div>Loading…</div> : this.renderTable()}
                {this.renderFooter()}
            </div>
        );
    }
}
